import { http } from './http';
import type {
    EmailCheckRequest,
    EmailCheckResponse,
    AddProfileRequest,
    VerifyEmailResponse,
    VerifyEmailRequest,
    RegisterRequest,
    ForgotPasswordRequest,
    ForgotPasswordResponse,
} from './types/account';

export const accountService = {
    login,
    logout,
    getAccount,
    checkEmail,
    addClientProfile,
    verifyEmail,
    register,
    forgotPassword,
};

interface LoginRequest {
    login: string;
    password: string;
}

export enum UserRole {
    Client = 0,
    Reseller = 1,
    Admin = 2,
}

export interface UserAccount {
    id: number;
    email: string;
    name: string;
    role: UserRole; // undefined for now
    jwtToken: string;
}

let account: UserAccount = null;

function getAccount(): UserAccount | null {
    // if account does not exist, try to get it from local storage
    if (!account) {
        const item = localStorage.getItem('account');
        if (item) {
            account = JSON.parse(item);
        }
    }
    return account;
}

function setAccount(data: UserAccount) {
    account = data;
    localStorage.setItem('account', JSON.stringify(data));
}

function removeAccount() {
    account = null;
    localStorage.removeItem('account');
}

function parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(
        window
            .atob(base64)
            .split('')
            .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join('')
    );

    return JSON.parse(jsonPayload);
}

function getRole(roleAsString: string): UserRole {
    if (roleAsString === 'Admin') {
        return UserRole.Admin;
    } else if (roleAsString === 'Reseller') {
        return UserRole.Reseller;
    } else if (roleAsString === 'Client') {
        return UserRole.Client;
    } else {
        return undefined;
    }
}

async function login(login: string, password: string): Promise<boolean> {
    const result = await http<UserAccount, LoginRequest>({
        path: '/accounts/authenticate',
        method: 'POST',
        body: { login, password },
    });
    if (result.ok) {
        var jwtToken = parseJwt(result.body.jwtToken);
        setAccount({
            id: jwtToken.Id,
            email: jwtToken.Email,
            name: jwtToken.FirstName + ' ' + jwtToken.LastName,
            role: getRole(jwtToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']),
            jwtToken: result.body.jwtToken,
        });
        // startRefreshTokenTimer();
        return true;
    } else {
        return false;
    }
}

async function logout() {
    // revoke token, stop refresh timer, publish null to user subscribers and redirect to login page
    // await http({
    //     path: '/accounts/revoke-token',
    //     method: 'POST',
    // });
    // stopRefreshTokenTimer();
    removeAccount();
}

async function refreshToken() {
    const result = await http({
        path: '/accounts/refresh-token',
        method: 'POST',
    });
    if (result.ok) {
        // const user = result.body;

        // account = user;
        startRefreshTokenTimer();
        return true;
    } else {
        return false;
    }
}

// helper functions

let refreshTokenTimeout;

function startRefreshTokenTimer() {
    // parse json object from base64 encoded jwt token
    const jwtToken = JSON.parse(atob(account.jwtToken.split('.')[1]));

    // set a timeout to refresh the token a minute before it expires
    const expires = new Date(jwtToken.exp * 1000);
    const timeout = expires.getTime() - Date.now() - 60 * 1000;

    // eslint-disable-next-line
    refreshTokenTimeout = setTimeout(refreshToken, timeout);
}

// function stopRefreshTokenTimer() {
//     clearTimeout(refreshTokenTimeout);
// }

async function checkEmail(email: string): Promise<EmailCheckResponse | null> {
    const result = await http<EmailCheckResponse, EmailCheckRequest>({
        path: `/accounts/check-email`,
        method: 'POST',
        body: { email },
    });

    if (result.ok) {
        return result.body;
    } else {
        return null;
    }
}

async function addClientProfile(email: string): Promise<boolean> {
    const result = await http<any, AddProfileRequest>({
        path: `/accounts/add-profile`,
        method: 'POST',
        body: { email },
    });
    return result.ok;
}

// Yes/no only. The registration link carries both the token and the email it
// was sent to; the API confirms the pair and never returns the email itself.
async function verifyEmail(token: string, email: string): Promise<boolean> {
    const result = await http<VerifyEmailResponse, VerifyEmailRequest>({
        path: '/accounts/verify-email',
        method: 'POST',
        body: { token, email },
    });
    return result.ok && result.body?.valid === true;
}

async function register(data: RegisterRequest): Promise<boolean> {
    const result = await http<any, RegisterRequest>({
        path: `/accounts/register`,
        method: 'POST',
        body: data,
    });
    return result.ok;
}

async function forgotPassword(email: string): Promise<ForgotPasswordResponse | null> {
    const result = await http<ForgotPasswordResponse, ForgotPasswordRequest>({
        path: `/accounts/forgot-password`,
        method: 'POST',
        body: { email },
    });

    if (result.ok) {
        return result.body;
    } else {
        return null;
    }
}
